import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Avatar,
  Stack,
  Tooltip,
  Checkbox,
  Link,
  Button,
  alpha,
  Popover,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Schedule as ScheduleIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Feedback as FeedbackIcon,
  TrendingUp as TrendingUpIcon,
  Event as EventIcon,
  LocationOn as LocationOnIcon,
  VideoCall as VideoCallIcon,
  PersonPin as PersonPinIcon,
  Group as GroupIcon,
  Computer as VirtualIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon,
  Image as ImageIcon,
  Place as PlaceIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getSiteVisitStatusColor, getSiteVisitTravelTypeColor, generateFormatedLink, getTruncatedTagDisplay } from '../../utils/helper';
import TableBadge from '../../components/common/TableBadge';
import TagList from '../../components/common/TagList';
import SiteVisitActions from './SiteVisitActions';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const SiteVisitList = ({
  visits = [],
  isLoading = false,
  searchKeyword,
  isSearchActive,
  onEditSiteVisit,
  onSiteVisitUpdated,
  handleReschedule,
  handleCompleteVisit,
  handleCancel,
  sortField = 'updated_at',
  sortOrder = 'desc',
  onSort,
}) => {
  const navigate = useNavigate();

  // State for tags popover
  const [tagsPopoverAnchor, setTagsPopoverAnchor] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);

  // State for image preview modal
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedImageVisit, setSelectedImageVisit] = useState(null);

  // Handle tags popover
  const handleTagsClick = (event, tags) => {
    event.stopPropagation();
    setTagsPopoverAnchor(event.currentTarget);
    setSelectedTags(tags || []);
  };

  const handleTagsPopoverClose = () => {
    setTagsPopoverAnchor(null);
    setSelectedTags([]);
  };

  // Handle image preview
  const handleImageClick = (event, imageUrl, visit) => {
    event.stopPropagation();
    setSelectedImageUrl(imageUrl);
    setSelectedImageVisit(visit);
    setImagePreviewOpen(true);
  };

  const handleImagePreviewClose = () => {
    setImagePreviewOpen(false);
    setSelectedImageUrl('');
    setSelectedImageVisit(null);
  };

  // Handle image download
  const handleImageDownload = async () => {
    try {
      const response = await fetch(selectedImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename from visit info
      const leadName = selectedImageVisit?.lead?.name || 'Unknown';
      const date = selectedImageVisit?.completed_at 
        ? dayjs(selectedImageVisit.completed_at).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
      const filename = `site-visit-${leadName.replace(/\s+/g, '-')}-${date}.jpg`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback: open in new tab
      window.open(selectedImageUrl, '_blank');
    }
  };

  // Helper function to get visit type styling
  const getVisitTypeConfig = (visitTypeVerbose) => {
    const type = visitTypeVerbose?.toLowerCase();
    if (type === 'virtual') {
      return {
        icon: <VirtualIcon fontSize="small" sx={{ color: '#1976d2' }} />,
        color: '#1976d2',
        background: '#e3f2fd',
        border: '#bbdefb',
        label: 'Virtual'
      };
    } else if (type === 'in-person') {
      return {
        icon: <GroupIcon fontSize="small" sx={{ color: '#2e7d32' }} />,
        color: '#2e7d32',
        background: '#e8f5e8',
        border: '#c8e6c9',
        label: 'In-Person'
      };
    }
    // Default fallback without icon
    return {
      icon: null,
      color: '#666',
      background: '#f5f5f5',
      border: '#ddd',
      label: visitTypeVerbose || 'Unknown'
    };
  };

  // Helper function to render sortable header
  const renderSortableHeader = (label, field) => {
    const isActive = sortField === field;
    const isAsc = sortOrder === 'asc';
    
    return (
      <Box
        onClick={() => onSort && onSort(field)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: onSort ? 'pointer' : 'default',
          userSelect: 'none',
          '&:hover': onSort ? {
            color: 'primary.main',
          } : {},
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: isActive ? 'primary.main' : 'text.primary',
          }}
        >
          {label}
        </Typography>
        {onSort && (
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.25 }}>
            {isActive ? (
              isAsc ? (
                <ArrowUpwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              )
            ) : (
              <UnfoldMoreIcon sx={{ fontSize: 16, color: 'text.disabled', opacity: 0.5 }} />
            )}
          </Box>
        )}
      </Box>
    );
  };


  return (
    <>
       <Paper 
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: (theme) => theme.shadows[2],
            transition: 'all 0.3s ease-in-out',
            position: 'relative',
            width: '100%',
          }}
        >
          <TableContainer 
            sx={{ 
              overflowX: 'auto',
              overflowY: 'visible',
              width: '100%',
              '&::-webkit-scrollbar': {
                height: 8
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 4,
                backgroundColor: 'rgba(0,0,0,0.2)'
              }
            }}
          >
          {/* Loading indicator - shows at top of table */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10,
                width: '100%',
              }}
            >
              <LinearProgress 
                color="primary"
                sx={{ 
                  height: 4,
                  '& .MuiLinearProgress-bar': {
                    transition: 'transform 0.2s linear',
                  },
                  '& .MuiLinearProgress-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  }
                }} 
              />
            </Box>
          )}
          <Table size="medium" sx={{ minWidth: 1400, tableLayout: 'auto' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[50] }}>
                {/* Created At - When visit was created */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '150px'
                  }}
                >
                  {renderSortableHeader('Created At', 'created_at')}
                </TableCell>
                {/* Scheduled For - When visit is scheduled */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '150px'
                  }}
                >
                  {renderSortableHeader('Scheduled For', 'scheduled_at')}
                </TableCell>
                {/* Completed At - Show when visit is completed */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '150px'
                  }}
                >
                  {renderSortableHeader('Completed At', 'completed_at')}
                </TableCell>
                {/* Lead - Customer info */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '200px'
                  }}
                >
                  {renderSortableHeader('Lead', 'lead_name')}
                </TableCell>
                {/* Agent - Assignment info */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '150px'
                  }}
                >
                  {renderSortableHeader('Agent', 'agent')}
                </TableCell>
                {/* Status - Critical for workflow */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '120px'
                  }}
                >
                  {renderSortableHeader('Status', 'status')}
                </TableCell>
                {/* Visit Image */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '100px'
                  }}
                >
                  Image
                </TableCell>
                {/* Visit Location */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '150px'
                  }}
                >
                  Location
                </TableCell>
                {/* Tags / Feedback - Secondary info */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '200px',
                  }}
                >
                  Tags / Feedback
                </TableCell>
                {/* Actions */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: '56px',
                    width: '56px',
                    pr: 1,
                  }}
                  align="center"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Loading site visits...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : visits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {isSearchActive && searchKeyword
                        ? `No site visits found for "${searchKeyword}"`
                        : 'No site visits found'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visits.map((visit, index) => {
                  const leadInfo = visit.lead || visit.calendar_item?.lead;
                  const agentInfo = visit.agent || visit.calendar_item?.agent;
                  const projectInfo = visit.project;
                  
                  return (
                    <TableRow
                      key={visit.id}
                      hover
                      sx={(theme) => ({
                        height: theme.spacing(9),
                        transition: 'background-color 0.2s ease',
                        borderLeft: '4px solid transparent',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&:last-child td, &:last-child th': { border: 0 },
                      })}
                    >
                      {/* Created At - Column 1 */}
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {visit.created_at ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={500} lineHeight={1.2} color="text.secondary">
                              {dayjs(visit.created_at).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                              {dayjs(visit.created_at).format('h:mm A')}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Scheduled For - Column 2 */}
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={500} lineHeight={1.2}>
                            {visit.scheduled_date || 'Not scheduled'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                            {visit.scheduled_time || 'No time set'}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Completed At - Column 3 */}
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {visit.status === 'completed' && visit.completed_at ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={500} lineHeight={1.2} color="success.main">
                              {dayjs(visit.completed_at).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="success.main" lineHeight={1.2}>
                              {dayjs(visit.completed_at).format('h:mm A')}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Lead - Column 4 */}
                      <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ verticalAlign: 'middle' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {/* Lead ID */}
                          {leadInfo?.lead_id && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: 'text.secondary',
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: '0.6rem',
                                width: 'fit-content'
                              }}
                            >
                              #{leadInfo.lead_id}
                            </Typography>
                          )}
                          {/* Lead Name - Enhanced styling */}
                          <Link
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/leads/${leadInfo.id}`);
                            }}
                            underline="none"
                            sx={{ 
                              fontWeight: 600,
                              color: 'primary.main',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                color: 'primary.dark',
                                textDecoration: 'underline',
                                textDecorationColor: 'primary.dark'
                              } 
                            }}
                          >
                            {leadInfo?.name || 'Unknown Lead'}
                          </Link>
                          {/* Contact Number */}
                          {leadInfo?.mobile && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: 'text.secondary'
                              }}
                            >
                              <PhoneIcon fontSize="tiny" />
                              {leadInfo.mobile}
                            </Typography>
                          )}
                          {!leadInfo?.mobile && leadInfo?.email && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: 'text.secondary'
                              }}
                            >
                              <MailIcon fontSize="tiny" />
                              {leadInfo.email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Agent - Column 3 */}
                      <TableCell 
                        sx={{ verticalAlign: 'middle' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Link
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/reports/agent/${agentInfo.id}`);
                            }}
                            underline="none"
                            sx={{ 
                              fontWeight: 600,
                              color: 'primary.main',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                color: 'primary.dark',
                                textDecoration: 'underline',
                                textDecorationColor: 'primary.dark'
                              } 
                            }}
                          >
                            {agentInfo?.name || 'Unknown Agent'}
                          </Link>
                          {agentInfo && agentInfo.is_active === false && (
                            <Chip
                              label="Inactive"
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                backgroundColor: '#ffebee',
                                color: '#c62828',
                                border: '1px solid #ef5350',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      {/* Status - Column 4 */}
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        <TableBadge
                          label={String(visit.status_verbose || 'Unknown')}
                          {...getSiteVisitStatusColor(visit.status)}
                          sx={{
                            width: 'fit-content',
                            minWidth: 'auto',
                            maxWidth: 'none',
                            fontWeight: 500,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            borderRadius: '4px'
                          }}
                        />
                      </TableCell>

                      {/* Visit Image - Column 6 */}
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {visit.completion_image_url ? (
                          <Tooltip title="Click to preview">
                            <Box
                              onClick={(e) => handleImageClick(e, visit.completion_image_url, visit)}
                              sx={{
                                cursor: 'pointer',
                                position: 'relative',
                                width: 60,
                                height: 60,
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '2px solid',
                                borderColor: 'divider',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  borderColor: 'primary.main'
                                }
                              }}
                            >
                              <img
                                src={visit.completion_image_url}
                                alt="Visit"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </Box>
                          </Tooltip>
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 1,
                              border: '1px dashed',
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.50'
                            }}
                          >
                            <ImageIcon sx={{ color: 'text.disabled', fontSize: 24 }} />
                          </Box>
                        )}
                      </TableCell>

                      {/* Visit Location - Column 7 */}
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {visit.completion_location ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {visit.completion_location.address ? (
                              <Tooltip title={visit.completion_location.address}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    color: 'text.primary',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  <PlaceIcon fontSize="small" sx={{ color: 'error.main' }} />
                                  {visit.completion_location.address}
                                </Typography>
                              </Tooltip>
                            ) : null}
                            {visit.completion_location.latitude && visit.completion_location.longitude && (
                              <Link
                                variant="caption"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const url = `https://www.google.com/maps?q=${visit.completion_location.latitude},${visit.completion_location.longitude}`;
                                  window.open(url, '_blank');
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  fontSize: '0.75rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                <LocationOnIcon fontSize="tiny" />
                                View on Map
                              </Link>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            No location
                          </Typography>
                        )}
                      </TableCell>

                      {/* Tags / Feedback - Column 8 */}
                      <TableCell sx={{ verticalAlign: 'middle', pr: 4 }}>
                        {visit.status === 'completed' ? (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary',
                              fontStyle: 'italic',
                              fontSize: '0.8rem',
                              lineHeight: 1.5,
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                            title={visit.agent_notes || 'No feedback provided'}
                          >
                            {visit.agent_notes || 'No feedback provided'}
                          </Typography>
                        ) : visit.tags?.length > 0 ? (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              gap: 0.5,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              p: 0.5,
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                              }
                            }} 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTagsClick(e, visit.tags);
                            }}
                            title="Click to view all tags"
                          >
                            {visit.tags.length > 0 && (
                              <TagList 
                                items={getTruncatedTagDisplay(visit.tags, 2)} 
                                color="#e91e63"
                              />
                            )}
                            {visit.tags.length > 2 && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                +{visit.tags.length - 2} more...
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            No Tags
                          </Typography>
                        )}
                      </TableCell>

                      {/* Actions - three dots menu */}
                      <TableCell
                        sx={{ verticalAlign: 'middle', pr: 1 }}
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SiteVisitActions
                          siteVisit={visit}
                          mode="list"
                          onSiteVisitUpdated={onSiteVisitUpdated}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

        <Popover
          open={Boolean(tagsPopoverAnchor)}
          anchorEl={tagsPopoverAnchor}
          onClose={handleTagsPopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              border: '1px solid',
              borderColor: 'divider',
              minWidth: 200,
              maxWidth: 300
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ 
              fontWeight: 600, 
              color: 'text.primary',
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              All Tags ({selectedTags.length})
            </Typography>
            {selectedTags.length > 0 ? (
              <TagList
                items={selectedTags}
                color="#e91e63"
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No tags assigned
              </Typography>
            )}
          </Box>
        </Popover>

        {/* Image Preview Modal */}
        <Dialog
          open={imagePreviewOpen}
          onClose={handleImagePreviewClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '90vh'
            }
          }}
        >
          <Box sx={{ 
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Visit Image Preview
            </Typography>
            <IconButton
              onClick={handleImagePreviewClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <DialogContent sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
            {selectedImageUrl && (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2
                }}
              >
                <img
                  src={selectedImageUrl}
                  alt="Visit Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {selectedImageVisit?.lead && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {selectedImageVisit.lead.name}
                </Typography>
                {selectedImageVisit.completed_at && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {dayjs(selectedImageVisit.completed_at).format('MMM DD, YYYY [at] h:mm A')}
                  </Typography>
                )}
              </Box>
            )}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleImageDownload}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              Download
            </Button>
          </DialogActions>
        </Dialog>
    </>
  );
};

export default SiteVisitList;